import { UserInfo } from '@empathy/search-types';
import { StorageService } from '@empathy/storage-service';
import { inject, injectable, named } from 'inversify';
import uuid from 'uuid/v4';
import { Store } from 'vuex';
import { DEPENDENCIES, STORAGE } from '../container/dependencies.const';
import { EmpathyXConfig, UserServiceStorageEntry } from '../models';
import { GETTERS_KEYS, RootStoreState } from '../store';
import { UserService } from './services.types';

@injectable()
export class LocalStorageUserService implements UserService {

  private get storageConfig() {
    return (this.store.getters[GETTERS_KEYS.config.config] as EmpathyXConfig).userServiceStorage;
  }

  constructor(
    @inject(DEPENDENCIES.Store) private readonly store: Store<RootStoreState>,
    @inject(DEPENDENCIES.StorageService) @named(STORAGE.Local) private readonly localStorage: StorageService) {}

  getUserInfo(): UserInfo {
    return {
      user: this.getUserId(),
      session: this.getSessionId(),
      userType: this.getUserType()
    };
  }

  removeUserInfo(): void {
    Object.values(this.storageConfig).forEach((entry: UserServiceStorageEntry) => {
      this.localStorage.removeItem(entry.key);
    });
  }

  private getUserId(): string {
    let userId = this.localStorage.getItem(this.storageConfig.userId.key);
    if (!userId) {
      userId = uuid();
      this.localStorage.setItem(this.storageConfig.userType.key, 'new', this.storageConfig.userType.ttl);
    }
    this.localStorage.setItem(this.storageConfig.userId.key, userId, this.storageConfig.userId.ttl);
    return userId;
  }

  private getSessionId(): string {
    // If the user exists, but user type has expired, then it means it is a recurrent user
    const userType = this.localStorage.getItem(this.storageConfig.userType.key) || 'recurrent';
    this.localStorage.setItem(this.storageConfig.userType.key, userType, this.storageConfig.userType.ttl);
    const sessionId = this.localStorage.getItem(this.storageConfig.sessionId.key) || uuid();
    this.localStorage.setItem(this.storageConfig.sessionId.key, sessionId, this.storageConfig.sessionId.ttl);
    return sessionId;
  }

  private getUserType(): string {
    return this.localStorage.getItem(this.storageConfig.userType.key);
  }
}
